/**
 * Loading Overlay Component
 *
 * Displays a loading overlay with spinner that can be controlled via custom events.
 * Multiple instances can exist on the same page, each with a unique data-loading-id.
 *
 * Usage:
 * <loading-overlay data-loading-id="unique-id"></loading-overlay>
 *
 * Control via events:
 * document.dispatchEvent(new CustomEvent('loading:start', { detail: { id: 'unique-id' } }));
 * document.dispatchEvent(new CustomEvent('loading:end', { detail: { id: 'unique-id' } }));
 */
class LoadingOverlay extends HTMLElement {
  #loadingId = null;
  #handleLoadingStartBound = null;
  #handleLoadingEndBound = null;

  connectedCallback() {
    this.#loadingId = this.getAttribute('data-loading-id');

    if (!this.#loadingId) {
      console.warn('LoadingOverlay requires data-loading-id attribute');
      return;
    }

    // Bind methods once for proper cleanup
    this.#handleLoadingStartBound = (e) => this.handleLoadingStart(e);
    this.#handleLoadingEndBound = (e) => this.handleLoadingEnd(e);

    this.render();

    // Listen for loading events on document
    document.addEventListener('loading:start', this.#handleLoadingStartBound);
    document.addEventListener('loading:end', this.#handleLoadingEndBound);
  }

  handleLoadingStart(e) {
    // Only respond if event ID matches this instance
    if (e.detail?.id === this.#loadingId) {
      this.show();
    }
  }

  handleLoadingEnd(e) {
    // Only respond if event ID matches this instance
    if (e.detail?.id === this.#loadingId) {
      this.hide();
    }
  }

  show() {
    this.ariaHidden = false;
  }

  hide() {
    this.ariaHidden = true;
  }

  render() {
    this.ariaHidden = true;
    this.classList.add('visible', 'group', 'absolute', 'inset-0', 'bg-[#fff]/5', 'backdrop-blur-sm', 'z-layer-raised', 'flex', 'justify-center', 'transition-[backdrop-filter,visibility]', 'duration-500', 'ease-out', 'aria-hidden:backdrop-blur-none', 'aria-hidden:invisible');

    // Render spinner SVG inline
    this.innerHTML = `
      <svg
        class="animate-spin block group-aria-hidden:hidden my-20 sticky top-1/2"
        width="32"
        height="32"
        viewBox="0 0 24 24"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
        aria-hidden="true"
      >
        <circle
          class="opacity-25"
          cx="12"
          cy="12"
          r="10"
          stroke="currentColor"
          stroke-width="3"
        ></circle>
        <path
          class="opacity-75"
          fill="currentColor"
          d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
        ></path>
      </svg>
    `;
  }

  disconnectedCallback() {
    if (this.#handleLoadingStartBound) {
      document.removeEventListener('loading:start', this.#handleLoadingStartBound);
    }
    if (this.#handleLoadingEndBound) {
      document.removeEventListener('loading:end', this.#handleLoadingEndBound);
    }
  }
}

if (!customElements.get('loading-overlay')) {
  customElements.define('loading-overlay', LoadingOverlay);
}
